/**
 * The SIP stack behind the softphone: what the phone needs of SIP.js, as
 * an interface, and the one implementation of it (#207 step 2).
 *
 * The phone's state machine (`use-softphone.ts`) and its screen never
 * import SIP.js. They talk to a `SipStack` — connect, place a call, hear
 * about one arriving — and a `SipCall` — answer, hang up, mute, send a
 * tone — and a test hands them a fake. This file is the only place the
 * library's vocabulary (UserAgent, Registerer, Inviter, Invitation,
 * SessionState) appears, so a SIP.js upgrade is a change here and
 * nowhere else.
 *
 * The server side of every call here is the Asterisk in infra/voice: the
 * browser registers as the endpoint intake wrote for this person
 * (ADR-0033) over WSS, and media is DTLS-SRTP straight to the server.
 * There is no TURN: the server is on a public address, so a browser that
 * can reach it over WebSocket can reach it over UDP, and ICE finds the
 * path without relaying. One STUN server is named so the browser learns
 * its own public address and offers it, which shortens ICE on networks
 * where it matters.
 */
import {
  Inviter,
  Invitation,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
  type Session,
} from "sip.js";
import type { SessionDescriptionHandler } from "sip.js/lib/platform/web";
import type { SoftphoneCredentials } from "./softphone-api";

export type CallDirection = "inbound" | "outbound";

/**
 * One call's state, as the phone draws it. `ringing` is an outbound call
 * waiting to be answered or an inbound one waiting for us; `answered` is
 * media both ways; `ended` is final, and `reason` says how.
 */
export type CallState = "ringing" | "answered" | "ended";

export interface SipCall {
  readonly direction: CallDirection;
  /** The other party as the server named them: a number, or a display name. */
  readonly remote: string;
  readonly state: CallState;
  /** Why an ended call ended, in a few words for the screen. */
  readonly endReason: string;
  /** Hears every state change; returns the unsubscribe. */
  subscribe(listener: (state: CallState) => void): () => void;
  /** Inbound only: take the call. */
  answer(): Promise<void>;
  /** Decline, cancel or hang up, whichever the state calls for. */
  hangup(): Promise<void>;
  setMuted(muted: boolean): void;
  /** One DTMF tone, 0-9 * #. */
  sendDTMF(tone: string): void;
}

export interface SipStackHandlers {
  onRegistered(): void;
  /** Registration lost, or the transport with it. `reason` is for the screen. */
  onUnregistered(reason: string): void;
  onIncoming(call: SipCall): void;
}

export interface SipStack {
  connect(creds: SoftphoneCredentials, handlers: SipStackHandlers): Promise<void>;
  disconnect(): Promise<void>;
  /** Place a call to a number under the registered domain. */
  call(target: string): Promise<SipCall>;
}

/** Where remote audio plays. The phone owns the element; the stack is handed a way to find it. */
export type AudioSink = () => HTMLAudioElement | null;

const STUN = "stun:stun.l.google.com:19302";

/** Reconnect backoff after an unexpected transport drop, in ms. */
const RECONNECT_STEPS = [1000, 2000, 4000, 8000, 15000, 30000];

/** The SIP.js implementation. */
export function createSipJsStack(audio: AudioSink): SipStack {
  let ua: UserAgent | null = null;
  let registerer: Registerer | null = null;
  let handlers: SipStackHandlers | null = null;
  let domain = "";
  let stopping = false;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function scheduleReconnect() {
    if (stopping || !ua || reconnectTimer) return;
    const delay = RECONNECT_STEPS[Math.min(reconnectAttempt, RECONNECT_STEPS.length - 1)];
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (stopping || !ua) return;
      ua.reconnect().catch(() => scheduleReconnect());
    }, delay);
  }

  return {
    async connect(creds, h) {
      handlers = h;
      domain = creds.domain;
      stopping = false;
      const uri = UserAgent.makeURI(`sip:${creds.username}@${creds.domain}`);
      if (!uri) throw new Error("softphone: bad endpoint address");
      ua = new UserAgent({
        uri,
        displayName: creds.display_name,
        authorizationUsername: creds.username,
        authorizationPassword: creds.password,
        transportOptions: { server: creds.wss_url },
        sessionDescriptionHandlerFactoryOptions: {
          iceGatheringTimeout: 500,
          peerConnectionConfiguration: { iceServers: [{ urls: STUN }] },
        },
        logLevel: "warn",
        delegate: {
          onConnect: () => {
            reconnectAttempt = 0;
            registerer?.register().catch(() => {
              handlers?.onUnregistered("Could not register");
            });
          },
          onDisconnect: (error) => {
            handlers?.onUnregistered(error ? "Connection lost" : "Disconnected");
            if (error) scheduleReconnect();
          },
          onInvite: (invitation) => {
            handlers?.onIncoming(wrapSession(invitation, "inbound", audio));
          },
        },
      });
      registerer = new Registerer(ua, { expires: 300 });
      registerer.stateChange.addListener((state) => {
        if (state === RegistererState.Registered) handlers?.onRegistered();
        else if (state === RegistererState.Unregistered) handlers?.onUnregistered("Not registered");
      });
      // start() connects the transport; onConnect registers. A start that
      // fails is the one error that surfaces here rather than through the
      // handlers: there is nothing to reconnect to yet.
      await ua.start();
    },

    async disconnect() {
      stopping = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      const r = registerer;
      const u = ua;
      registerer = null;
      ua = null;
      handlers = null;
      try {
        if (r && r.state === RegistererState.Registered) await r.unregister();
      } catch {
        // Leaving anyway.
      }
      await u?.stop().catch(() => {});
    },

    async call(target) {
      if (!ua) throw new Error("softphone: not connected");
      const uri = UserAgent.makeURI(`sip:${target}@${domain}`);
      if (!uri) throw new Error("softphone: bad number");
      const inviter = new Inviter(ua, uri, {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
      const call = wrapSession(inviter, "outbound", audio, target);
      await inviter.invite();
      return call;
    },
  };
}

/**
 * One SIP.js session as a SipCall. Handles the three verbs whose name
 * depends on the state — cancel before an answer, reject before ours,
 * bye after either — and attaches the remote audio once there is any.
 */
function wrapSession(session: Session, direction: CallDirection, audio: AudioSink, dialled?: string): SipCall {
  const listeners = new Set<(s: CallState) => void>();
  let state: CallState = "ringing";
  let endReason = "";
  let muted = false;

  const remote = dialled ?? session.remoteIdentity.displayName ?? session.remoteIdentity.uri.user ?? "Unknown";

  const sdh = () => session.sessionDescriptionHandler as SessionDescriptionHandler | undefined;

  function set(next: CallState) {
    if (state === next) return;
    state = next;
    for (const l of listeners) l(next);
  }

  function attachAudio() {
    const pc = sdh()?.peerConnection;
    const el = audio();
    if (!pc || !el) return;
    const stream = new MediaStream();
    for (const receiver of pc.getReceivers()) {
      if (receiver.track) stream.addTrack(receiver.track);
    }
    el.srcObject = stream;
    el.play().catch(() => {});
  }

  function applyMute() {
    const pc = sdh()?.peerConnection;
    if (!pc) return;
    for (const sender of pc.getSenders()) {
      if (sender.track) sender.track.enabled = !muted;
    }
  }

  session.stateChange.addListener((s) => {
    switch (s) {
      case SessionState.Established:
        attachAudio();
        applyMute();
        set("answered");
        break;
      case SessionState.Terminated:
        if (!endReason) endReason = state === "answered" ? "Call ended" : direction === "inbound" ? "Missed" : "No answer";
        set("ended");
        break;
      default:
        break;
    }
  });

  return {
    direction,
    remote,
    get state() {
      return state;
    },
    get endReason() {
      return endReason;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async answer() {
      if (!(session instanceof Invitation)) return;
      await session.accept({ sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } } });
    },
    async hangup() {
      switch (session.state) {
        case SessionState.Initial:
        case SessionState.Establishing:
          endReason = direction === "inbound" ? "Declined" : "Cancelled";
          if (session instanceof Inviter) await session.cancel();
          else if (session instanceof Invitation) await session.reject();
          break;
        case SessionState.Established:
          endReason = "Call ended";
          await session.bye();
          break;
        default:
          break;
      }
    },
    setMuted(next) {
      muted = next;
      applyMute();
    },
    sendDTMF(tone) {
      if (!/^[0-9*#]$/.test(tone)) return;
      sdh()?.sendDtmf(tone);
    },
  };
}
